import { Toggle } from '@amelio/ui-library';
import { useId } from 'react';

import type { ReactNode } from 'react';

/**
 * Indent of the help text and of the state, so both line up under the label rather than under the
 * switch. Derived from the single geometry the row is built on: the `smPlus` track (`w-[44px]` in
 * `Toggle`) plus the gap `Toggle` puts between the track and its label (`gap-3`, which the module's
 * 4px step renders at 12px). The two terms stay written out so the derivation is readable.
 */
const LABEL_INDENT = 'ml-[calc(44px+12px)]';

interface PillarSettingRowProps {
    label: string;
    /** What turning the setting off requires. Reads as the label's own sentence, not the group's. */
    help?: string;
    isChecked: boolean;
    isDisabled?: boolean;
    onChange: (isChecked: boolean) => void;
    /** What this setting currently amounts to, such as how many objectives carry no pillar. */
    state?: ReactNode;
    dataTestId: string;
}

/** One company setting: its switch, the help that belongs to it, and the state it governs. */
export default function PillarSettingRow({
    label,
    help,
    isChecked,
    isDisabled = false,
    onChange,
    state,
    dataTestId,
}: PillarSettingRowProps) {
    const helpId = useId();

    return (
        <div
            className={
                'flex flex-col gap-3 py-4 sm:flex-row sm:items-start sm:justify-between sm:gap-6'
            }
        >
            <div className={'flex flex-col gap-1.5'}>
                <Toggle
                    checked={isChecked}
                    onChange={onChange}
                    disabled={isDisabled}
                    size={'smPlus'}
                    rightLabel={label}
                    ariaDescribedBy={help ? helpId : undefined}
                    dataTestId={dataTestId}
                />

                {help && (
                    <p
                        id={helpId}
                        className={`${LABEL_INDENT} text-base-500 text-[13px] leading-[19px]`}
                    >
                        {help}
                    </p>
                )}
            </div>

            {state && <div className={`${LABEL_INDENT} shrink-0 sm:ml-0`}>{state}</div>}
        </div>
    );
}
